export default (shopId, subscriptionData) => ({
  url: null,
  shopId,
  subscriptionData,
  subscriptionCheckoutIntentKey: 'dsc_pending_subscription_checkout',
  async markSubscriptionCheckoutIntentIfNeeded() {
    try {
      const response = await fetch(`${window.location.origin}/cart.js`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      if (!response.ok) return
      const cart = await response.json()
      const hasSubscription = Boolean(
        cart?.items?.some((item) => item?.selling_plan_allocation || item?.selling_plan),
      )
      if (!hasSubscription) return
      localStorage.setItem(
        this.subscriptionCheckoutIntentKey,
        JSON.stringify({ at: Date.now() }),
      )
    } catch (error) {
      // Best-effort marker only; checkout redirect should continue regardless.
      console.warn('[Banner Cart API] Failed to mark subscription checkout intent:', error);
    }
  },
  async redirectToCheckoutWithIntent(path) {
    await this.markSubscriptionCheckoutIntentIfNeeded()
    window.location.assign(`${window.location.origin}${path}`)
  },
  cartApiHandler(event) {
    event.stopImmediatePropagation()
    this.url = event.target.getAttribute('href')

    if (this.url.includes('cart/add?')) {
      this.addToCart(event)
      return
    }

    if (this.url.includes('cart/') && !this.url.includes('cart/add?')) {
      this.addSellingPlanDiscountsToCart(event)
      return
    }
  },
  // Example: https://engineering-release.myshopify.com/cart/add?id=44056852267220&selling_plan=2899869908
  addToCart(event) {
    const formatUrlForParams = new URL(this.url)
    const params = new URLSearchParams(formatUrlForParams.search)
    const id = params.get('id')
    const selling_plan = params.get('selling_plan')

    const formData = {
      items: [
        {
          id,
          selling_plan,
        },
      ],
    }

    fetch(window.location.origin + '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then(async () => {
        const { checkoutRedirect } = event.target.dataset

        if (checkoutRedirect === 'true') {
          await this.redirectToCheckoutWithIntent('/checkout')
          return
        }

        window.location.assign(window.location.origin + '/cart')
      })
      .catch((error) => {
        window.console.error('Error:', error)
      })
  },

  // Example: https://us.dollarshaveclub.com/cart/40998774898736:1,40643312779312:1?discount=HEAVYDISCOUNT,HMFS
  addSellingPlanDiscountsToCart(event) {
    const { checkoutRedirect, shipSooner } = event.target.dataset;
    const [productsPartOfUrl, discountsPartOfUrl] = this.url.split('?')
    const discountCode = discountsPartOfUrl?.includes('discount=') ? discountsPartOfUrl.split('discount=')[1] : null
    const products = productsPartOfUrl.split('/cart/')[1].split(',')
    const formData = this.getSellingPlanFormData(products, shipSooner)
    
    fetch(window.location.origin + '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: formData,
      }),
    })
      .then(async () => {
        let redirectPath = '/cart'

        if (checkoutRedirect === 'true') {
          redirectPath = discountCode ? `/checkout?discount=${discountCode}` : '/checkout'
          await this.redirectToCheckoutWithIntent(redirectPath)
          return
        } else if (discountCode) {
          const firstDiscountCode = discountCode.split(',')[0]
          redirectPath = `/discount/${firstDiscountCode}?redirect=/cart`
        }

        window.location.assign(`${window.location.origin}${redirectPath}`)
      })
      .catch((error) => {
        window.console.error('Error:', error)
      })
  },

  getSellingPlanFormData(products, shipSooner) {
    
    if (shipSooner === 'true') {
      return products.map((product) => {
        const productWithQuantity = product.split(':')
        const id = productWithQuantity[0]
        const quantity = productWithQuantity[1]

        const matchingSubProduct = this.subscriptionData.products?.find((subProduct) => {
          return subProduct.product_id === id || subProduct.variant_id === id
        })

        // Get current date
        const currentDate = new Date()
        // Add 14 days (2 weeks)
        const twoWeeksFromNow = new Date(currentDate.getTime() + 14 * 24 * 60 * 60 * 1000)
        // Format as YYYY-MM-DD
        const formattedDate = twoWeeksFromNow.toISOString().split('T')[0]
        
        const sellingPlanId = matchingSubProduct && this.subscriptionData.selling_plans[matchingSubProduct.cadence];
        
        return {
          id: Number(id),
          quantity: Number(quantity),
          properties: {
            __og_first_order_place_date: formattedDate,
          },
          ...(sellingPlanId && {
            selling_plan: sellingPlanId,
          }),
        };
      })
    } else {
      return products.map((product) => {
        const productWithQuantity = product.split(':')
        const id = productWithQuantity[0]
        const quantity = productWithQuantity[1]

        const matchingSubProduct = this.subscriptionData.products?.find((subProduct) => {
          return subProduct.product_id === id || subProduct.variant_id === id
        })

        const sellingPlanId = matchingSubProduct && this.subscriptionData.selling_plans[matchingSubProduct.cadence];

        return {
          id: Number(id),
          quantity: Number(quantity),
          ...(sellingPlanId && {
            selling_plan: sellingPlanId,
          }),
        }
      })
    }
  },

  getAddedSubtotal(items) {
    let subtotal = 0

    items.forEach((item) => {
      // if item is already in cart, get the value of the item just added, ignoring what's already in cart
      if (item.quantity > 1) {
        const itemPrice = item.final_line_price / item.quantity
        subtotal = subtotal + itemPrice
      } else {
        subtotal = subtotal + item.final_line_price
      }
    })

    return subtotal / 100 // divide to get decimal value
  },

  getFormattedItems(items) {
    return items.map((item) => ({
      item_id: item.id,
      item_name: item.title,
      item_category: item.product_type,
      quantity: item.quantity,
      price: item.price / 100,
    }))
  },
})
